import { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link
} from '@mui/material'
import {
  useRepairCosts,
  useAssignRepairCost,
  useMarkRepairCostAsDone
} from '@services/helpdesk/helpdesk.hooks'

interface HelpdeskRepairCostsProps {
  ticketId: number
}

const HelpdeskRepairCosts = ({ ticketId }: HelpdeskRepairCostsProps) => {
  const { data: repairCostsData, isLoading, error } = useRepairCosts(ticketId)
  const { mutate: assignRepairCost, isPending: isAssigning } = useAssignRepairCost()
  const { mutate: markAsDone, isPending: isMarking } = useMarkRepairCostAsDone()

  const [selectedCostId, setSelectedCostId] = useState<number | null>(null)
  const [openAssignDialog, setOpenAssignDialog] = useState(false)
  const [openDoneDialog, setOpenDoneDialog] = useState(false)

  // Get repair costs array from paginated response
  const repairCosts = repairCostsData?.results || []

  const handleAssignClick = (repairCostId: number) => {
    setSelectedCostId(repairCostId)
    setOpenAssignDialog(true)
  }

  const handleDoneClick = (repairCostId: number) => {
    setSelectedCostId(repairCostId)
    setOpenDoneDialog(true)
  }

  const handleAssignConfirm = () => {
    if (selectedCostId) {
      assignRepairCost({ ticketId, repairCostId: selectedCostId })
      setOpenAssignDialog(false)
    }
  }

  const handleDoneConfirm = () => {
    if (selectedCostId) {
      markAsDone({ ticketId, repairCostId: selectedCostId })
      setOpenDoneDialog(false)
    }
  }

  // Funkcja pomocnicza do sprawdzania, czy plik jest obrazem na podstawie rozszerzenia URL
  const isImageFile = (url: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
    const lowerCaseUrl = url.toLowerCase()
    return imageExtensions.some((ext) => {
      // Sprawdź, czy URL zawiera rozszerzenie pliku obrazu przed parametrami query
      const extensionIndex = lowerCaseUrl.indexOf(ext)
      if (extensionIndex === -1) return false

      // Sprawdź, czy po rozszerzeniu jest koniec URL lub znak zapytania (?)
      const afterExtension = lowerCaseUrl.charAt(extensionIndex + ext.length)
      return afterExtension === '' || afterExtension === '?'
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ my: 2 }}>
        Wystąpił błąd podczas ładowania kosztów naprawy: {(error as Error).message}
      </Alert>
    )
  }

  if (repairCosts.length === 0) {
    return (
      <Alert severity='info' sx={{ my: 2 }}>
        Brak kosztów naprawy dla tego zgłoszenia.
      </Alert>
    )
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {repairCosts.map((cost) => (
          <Grid item xs={12} key={cost.id}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2
                  }}
                >
                  <Typography variant='h6'>{cost.repairType.name}</Typography>
                  <Chip
                    label={cost.done ? 'Wykonane' : 'Do wykonania'}
                    color={cost.done ? 'success' : 'warning'}
                    size='small'
                  />
                </Box>
                <Grid container spacing={2}>
                  {cost.comment && (
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' color='text.secondary'>
                        Komentarz
                      </Typography>
                      <Typography variant='body1' gutterBottom>
                        {cost.comment}
                      </Typography>
                    </Grid>
                  )}
                  {cost.file && (
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' color='text.secondary'>
                        Załącznik
                      </Typography>
                      {isImageFile(cost.file) ? (
                        <Box sx={{ my: 1, maxWidth: '100%' }}>
                          <img
                            src={cost.file}
                            alt='Załącznik'
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                          />
                        </Box>
                      ) : (
                        <Link href={cost.file} target='_blank' rel='noopener noreferrer'>
                          Pobierz załącznik
                        </Link>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Technik
                    </Typography>
                    <Typography variant='body1' gutterBottom>
                      {cost.technician ? cost.technician.fullName : 'Nieprzypisany'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                      {!cost.technician && (
                        <Button
                          variant='outlined'
                          color='primary'
                          onClick={() => handleAssignClick(cost.id)}
                          disabled={isAssigning}
                        >
                          Przypisz do mnie
                        </Button>
                      )}
                      {cost.technician && !cost.done && (
                        <Button
                          variant='contained'
                          color='success'
                          onClick={() => handleDoneClick(cost.id)}
                          disabled={isMarking}
                        >
                          Oznacz jako wykonane
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog potwierdzenia przypisania kosztu naprawy */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)}>
        <DialogTitle>Przypisz koszt naprawy</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz przypisać ten koszt naprawy do siebie?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Anuluj</Button>
          <Button onClick={handleAssignConfirm} variant='contained' color='primary' autoFocus>
            Przypisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog potwierdzenia oznaczenia kosztu naprawy jako wykonanego */}
      <Dialog open={openDoneDialog} onClose={() => setOpenDoneDialog(false)}>
        <DialogTitle>Oznacz jako wykonane</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz oznaczyć ten koszt naprawy jako wykonany?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDoneDialog(false)}>Anuluj</Button>
          <Button onClick={handleDoneConfirm} variant='contained' color='success' autoFocus>
            Oznacz jako wykonane
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default HelpdeskRepairCosts
